// React Router paths. The router (src/router.tsx) is the canonical
// definer; this file mirrors the path strings as named constants so
// inline <Navigate to="…"> / navigate("…") callsites elsewhere stop
// duplicating literals.
//
// Add a constant when a path is referenced outside the router or in
// more than one place. Per-route handlers that only spell the path
// once (inside the router) can keep the inline literal — naming it
// without callers buys nothing.

export const ROUTES = {
  // Public surfaces
  landing: "/",
  buy: "/buy",
  rent: "/rent",
  auth: "/auth",
  login: "/login",
  signup: "/signup",
  account: "/account",

  // Consumer shell
  chat: "/chat",
  explore: "/explore",
  workspace: "/workspace",
  marketWatch: "/market-watch",

  // Pro shell — top-level
  proDashboard: "/pro/dashboard",
  proCases: "/pro/cases",
  proInbox: "/pro/inbox",
  proReports: "/pro/reports",
  proLeads: "/pro/leads",
  proArticles: "/pro/articles",
  proCopilot: "/pro/copilot",
  proCrm: "/pro/crm",
  proPitch: "/pro/pitch",

  // Pro case-centric dedicated pages (PROP-218 spike → per-surface builds).
  // Flat routes under AppShellAgent. These superseded the legacy agent
  // workspace sub-shell, torn down in PROP-222.
  proListings: "/pro/listings",
  proOffers: "/pro/offers",
  proCalendar: "/pro/calendar",
  proInboxThreads: "/pro/inbox/threads",
} as const;

// Path builders for dynamic segments. Centralised so callers don't
// reassemble `/chat/${id}` inline and miss e.g. encodeURIComponent.
export const ROUTE_BUILDERS = {
  chatConversation: (conversationId: string) =>
    `/chat/${encodeURIComponent(conversationId)}`,
  workspaceById: (wsId: string) => `/workspace/${encodeURIComponent(wsId)}`,
  workspaceModule: (wsId: string, module: string) =>
    `/workspace/${encodeURIComponent(wsId)}/${module}`,
  linkAgent: (agentId: string) =>
    `/link/agent/${encodeURIComponent(agentId)}`,
  proCase: (caseId: string) => `/pro/cases/${encodeURIComponent(caseId)}`,
  proArticleById: (articleId: string) =>
    `/pro/articles/${encodeURIComponent(articleId)}`,
  // Case-centric agent surfaces (PROP-218 spike). proListing is owned by the
  // Listings build task; consumed by Offers/Calendar/ThreadDetail deep-links.
  proListing: (id: string) => `/pro/listings/${encodeURIComponent(id)}`,
  proListingBid: (listingId: string, bidId: string) =>
    `/pro/listings/${encodeURIComponent(listingId)}?tab=bids#bid-${bidId}`,
  proInboxThread: (id: string) =>
    `/pro/inbox/threads/${encodeURIComponent(id)}`,
} as const;
